import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { InMemoryCacheService, MessageType, UtilsService } from 'src/app/common';
import { PageLinksRequest, PageRequest, PagesByResourceRequest, ResourceRequest } from '../models/on-page';
import { SeoApiService } from '../seo-api.service';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class OnPageService {

  private _auditDetails: any;
  private _auditPages: any;
  private _pageSummaryInfo: any[];
  private _selectedPage: any;
  private _scanedPages: any[];

  constructor(private seoApiService: SeoApiService, private utils: UtilsService, private translate: TranslateService,
    private httpClient: HttpClient, private cache: InMemoryCacheService) { }

  public async getSummary(payload: any,):Promise<any> {
    this.cache.loading = true;
    const {data, error} = await this.seoApiService.getPageSummary(payload);
    this.cache.loading = false;
    this._auditDetails = data;
    return data;
  }

  public async getPages(payload: PageRequest, scanId: number):Promise<any> {
    this.cache.loading = true;
    const {data, error} = await this.seoApiService.getPages(payload, scanId);
    this.cache.loading = false;
    if (error) {
      this.utils.errorMessage(MessageType.TOAST, `There was an issue getting the requested pages.`);
      this.utils.logger.error(`There was an issue getting the requested pages.`, error);
    } else {
      this._auditPages = data;
      this.getPageSummaryInfo();
    }

    return {data, error};
  }

  public async getPageLinks(payload: PageLinksRequest, scanId: number):Promise<any> {
    return await this.seoApiService.getPageLinks(payload, scanId);;
  }

  public async getPagesByResource(payload: PagesByResourceRequest, scanId: number):Promise<any> {
    return await this.seoApiService.getPagesByResource(payload, scanId);;
  }

  public async getResources(payload: ResourceRequest, scanId: number):Promise<any> {
    return await this.seoApiService.getPageResources(payload, scanId);;
  }

  public async getLatestScan(payload: string):Promise<any> {
    const scanRecord = await this.seoApiService.getLatestScan(payload);
    return scanRecord;
  }

  public async getPageSpeedInsights(url: string, strategy: string):Promise<any> {
    const pageSpeedInsights = await this.seoApiService.getPageSpeedInsights(url, strategy);
    return pageSpeedInsights;
  }

  public getHowToFix(page: string): Observable<any> {
    return this.httpClient.get(`assets/site-audit-fixes/${page}.html`, {responseType: 'text'});
  }

  private getPageSummaryInfo() {
    this._pageSummaryInfo = [];
    this.auditPages?.items?.forEach(page => {
      const issueCount = [
        page?.brokenLinks,
        page?.brokenResources,
        page?.duplicateTitle,
        page?.duplicateDescription,
        page?.duplicateContent
      ].filter(Boolean).length;

      const infoItem = {    
        url: page?.url,
        title: page?.meta?.title,
        score: page?.onPageScore,
        statusCode: page?.statusCode,
        issueCount,
        internalLinks: page?.meta?.internalLinksCount,
        externalLinks: page?.meta?.externalLinksCount
      }

      this._pageSummaryInfo.push(infoItem);
    });
    return this._pageSummaryInfo;
  }

  set selectedPage(value: any) {
    this._selectedPage = value;
  }

  get selectedPage(): any {
    return this._selectedPage;
  }

  get auditDetails() {
    return this._auditDetails;
  }

  get auditPages() {
    return this._auditPages;
  }

  get summaryInfo() {
    return {
      pagesCrawled: this.auditDetails?.crawlStatus?.pagesCrawled,
      pagesInQueue: this.auditDetails?.crawlStatus?.pagesInQueue,
      maxCrawlPages: this.auditDetails?.crawlStatus?.maxCrawlPages,
      crawlProgress: this.auditDetails?.crawlProgress,
      lastCrawlTime: this.auditDetails?.domainInfo?.crawlStart,
      domainCrawled: this.auditDetails?.domainInfo?.mainDomain,
      score: this.auditDetails?.pageMetrics?.onPageScore
    }
  }

  get pageSummaryInfo() {   
    return this._pageSummaryInfo;
  }

  get siteLevelIssues() {
    return [{
          "name": this.translate.instant('onpage.summaryAudit.sitemap'),
          "indicator": this.auditDetails?.domainInfo?.checks?.sitemap ? 'FOUND' : 'NOT_FOUND',
          "details": this.translate.instant('onpage.summaryAudit.sitemapDetails'),
          "description": "",
          "howToFixPath":"sitemap-notfound"
      },
      {
          "name": this.translate.instant('onpage.summaryAudit.robots'),
          "indicator": this.auditDetails?.domainInfo?.checks?.robotsTxt ? 'FOUND' : 'NOT_FOUND',
          "details": this.translate.instant('onpage.summaryAudit.robotsDetails'),
          "description": "",
          "howToFixPath":"invalid-robots-txt-file"
      },{
          "name": this.translate.instant('onpage.summaryAudit.sslCertificate'),
          "indicator": this.auditDetails?.domainInfo?.checks?.ssl ? 'FOUND' : 'NOT_FOUND',
          "details": this.translate.instant('onpage.summaryAudit.sslCertificateDetails'),
          "description": "",
          "howToFixPath":"invalid-ssl-certificate"
      },{
          "name": this.translate.instant('onpage.summaryAudit.favicon'),
          "indicator": this.auditDetails?.pageMetrics?.checks?.noFavicon <= 0 ? 'FOUND' : 'NOT_FOUND',
          "details": this.translate.instant('onpage.summaryAudit.faviconDetails'),
          "description": "",
          "howToFixPath":"no-favicon"
    }]
  }

  get pageLevelIssues() {
    return [
      {
        "name": `${this.translate.instant('onpage.summaryAudit.titleDuplicates')}: ${this.auditDetails?.pageMetrics?.duplicateTitle ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.duplicateTitle === 0 ?  'PASSED' : 'WARNING',
        "description": this.auditDetails?.pageMetrics?.duplicateTitle,
        "filter": "['duplicate_title', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.titleDuplicates'),
        "howToFixPath":"title-duplicates"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.duplicateDescription')}: ${this.auditDetails?.pageMetrics?.duplicateDescription ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.duplicateDescription === 0 ? 'PASSED' : 'WARNING',
        "description": this.auditDetails?.pageMetrics?.duplicateDescription,
        "filter": "['duplicate_description', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.duplicateDescription'),
        "howToFixPath":"duplicate-description"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.duplicateContent')}: ${this.auditDetails?.pageMetrics?.duplicateContent ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.duplicateContent === 0 ? 'PASSED' : 'NOTICE',
        "description": this.auditDetails?.pageMetrics?.duplicateContent,
        "filter": "['duplicate_content', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.duplicateContent'),
        "howToFixPath":"duplicate-content"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.brokenLinks')}: ${this.auditDetails?.pageMetrics?.brokenLinks ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.brokenLinks === 0 ? 'PASSED' : 'CRITICAL',
        "description": this.auditDetails?.pageMetrics?.brokenLinks,
        "filter": "['broken_links', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.brokenLinks'),
        "howToFixPath":"broken-links"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.brokenResources')}: ${this.auditDetails?.pageMetrics?.brokenResources ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.brokenResources === 0 ? 'PASSED' : 'WARNING',
        "description": this.auditDetails?.pageMetrics?.brokenResources,
        "filter": "['broken_resources', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.brokenResources'),
        "howToFixPath":"broken-resources"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.linkRelationConflict')}: ${this.auditDetails?.pageMetrics?.linksRelationConflict ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.linksRelationConflict === 0 ? 'PASSED' : 'CRITICAL',
        "description": this.auditDetails?.pageMetrics?.linksRelationConflict,
        "filter": "['checks.is_link_relation_conflict', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.linkRelationConflict'),
        "howToFixPath":"link-relation-conflict"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.canonicalChain')}: ${this.auditDetails?.pageMetrics?.checks?.canonicalChain ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.canonicalChain === 0 ? 'PASSED' : 'WARNING',
        "description": this.auditDetails?.pageMetrics?.checks?.canonicalChain,
        "filter": "['checks.canonical_chain', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.canonicalChain'),
        "howToFixPath":"canonical-chain"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.canonicalToRedirect')}: ${this.auditDetails?.pageMetrics?.checks?.canonicalToRedirect ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.canonicalToRedirect === 0 ? 'PASSED' : 'WARNING',
        "description": this.auditDetails?.pageMetrics?.checks?.canonicalToRedirect,
        "filter": "['checks.canonical_to_redirect', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.canonicalToRedirect'),
        "howToFixPath":"canonical-to-redirect"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.highLoadingTime')}: ${this.auditDetails?.pageMetrics?.checks?.highLoadingTime ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.highLoadingTime === 0 ? 'PASSED' : 'WARNING',
        "description": this.auditDetails?.pageMetrics?.checks?.highLoadingTime,
        "filter": "['checks.high_loading_time', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.highLoadingTime'),
        "howToFixPath":"high-loading-time"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.redirectLoop')}: ${this.auditDetails?.pageMetrics?.redirectLoop ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.redirectLoop === 0 ? 'PASSED' : 'CRITICAL',
        "description": this.auditDetails?.pageMetrics?.redirectLoop,
        "filter": "['checks.redirect_chain', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.redirectLoop'),
        "howToFixPath":"redirect-loop"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.canonicalToBroken')}: ${this.auditDetails?.pageMetrics?.checks?.canonicalToBroken ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.canonicalToBroken === 0 ? 'PASSED' : 'CRITICAL',
        "description": this.auditDetails?.pageMetrics?.checks?.canonicalToBroken,
        "filter": "['checks.canonical_to_broken', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.canonicalToBroken'),
        "howToFixPath":"canonical-to-broken"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.recursiveCanonical')}: ${this.auditDetails?.pageMetrics?.checks?.recursiveCanonical ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.recursiveCanonical === 0 ? 'PASSED' : 'WARNING',
        "description": this.auditDetails?.pageMetrics?.checks?.recursiveCanonical,
        "filter": "['checks.recursive_canonical', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.recursiveCanonical'),
        "howToFixPath":"recursive-canonical"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.duplicateMetaTags')}: ${this.auditDetails?.pageMetrics?.checks?.duplicateMetaTags ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.duplicateMetaTags === 0 ? 'PASSED' : 'WARNING',
        "description": this.auditDetails?.pageMetrics?.checks?.duplicateMetaTags,
        "filter": "['checks.duplicate_meta_tags', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.duplicateMetaTags'),
        "howToFixPath":"duplicate-meta-tags"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.noDescription')}: ${this.auditDetails?.pageMetrics?.checks?.noDescription ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.noDescription === 0 ? 'PASSED' : 'WARNING',
        "description": this.auditDetails?.pageMetrics?.checks?.noDescription,
        "filter": "['checks.no_description', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.noDescription'),
        "howToFixPath":"no-description"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.largePageSize')}: ${this.auditDetails?.pageMetrics?.checks?.largePageSize ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.largePageSize === 0 ? 'PASSED' : 'WARNING',
        "description": this.auditDetails?.pageMetrics?.checks?.largePageSize,
        "filter": "['checks.large_page_size', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.largePageSize'),
        "howToFixPath":"large-page-size"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.isHttp')}: ${this.auditDetails?.pageMetrics?.checks?.isHttp ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.isHttp === 0 ? 'PASSED' : 'CRITICAL',
        "description": this.auditDetails?.pageMetrics?.checks?.isHttp,
        "filter": "['checks.is_http', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.isHttp'),
        "howToFixPath":"is-http"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.titleToLong')}: ${this.auditDetails?.pageMetrics?.checks?.titleTooLong ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.titleTooLong === 0 ? 'PASSED' : 'WARNING',
        "description": this.auditDetails?.pageMetrics?.checks?.title_too_long,
        "filter": "['checks.title_too_long', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.titleToLong'),
        "howToFixPath":"title-too-long"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.lowContentRate')}: ${this.auditDetails?.pageMetrics?.checks?.lowContentRate ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.lowContentRate === 0 ? 'PASSED' : 'NOTICE',
        "description": this.auditDetails?.pageMetrics?.checks?.lowContentRate,
        "filter": "['checks.low_content_rate', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.lowContentRate'),
        "howToFixPath":"low-content-rate"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.noH1Tag')}: ${this.auditDetails?.pageMetrics?.checks?.noH1Tag ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.noH1Tag === 0 ? 'PASSED' : 'WARNING',
        "description": this.auditDetails?.pageMetrics?.checks?.noH1Tag,
        "filter": "['checks.no_h1_tag', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.noH1Tag'),
        "howToFixPath":"no-h1-tag"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.noImageAlt')}: ${this.auditDetails?.pageMetrics?.checks?.noImageAlt ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.noImageAlt === 0 ? 'PASSED' : 'WARNING',
        "description": this.auditDetails?.pageMetrics?.checks?.noImageAlt,
        "filter": "['checks.no_image_alt', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.noImageAlt'),
        "howToFixPath":"no-image-alt"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.titleToShort')}: ${this.auditDetails?.pageMetrics?.checks?.titleTooShort ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.titleTooShort === 0 ? 'PASSED' : 'WARNING',
        "description": this.auditDetails?.pageMetrics?.checks?.titleTooShort,
        "filter": "['checks.title_too_short', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.titleToShort'),
        "howToFixPath":"title-too-short"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.is400Code')}: ${this.auditDetails?.pageMetrics?.checks?.is4xxCode ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.is4xxCode === 0 ? 'PASSED' : 'WARNING',
        "description": this.auditDetails?.pageMetrics?.checks?.is4xxCode,
        "filter": "['checks.is_4xx_code', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.is400Code'),
        "howToFixPath":"is-4xx-code"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.is500Code')}: ${this.auditDetails?.pageMetrics?.checks?.is5xxCode ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.is5xxCode === 0 ? 'PASSED' : 'CRITICAL',
        "description": this.auditDetails?.pageMetrics?.checks?.is5xxCode,
        "filter": "['checks.is_5xx_code', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.is500Code'),
        "howToFixPath":"is-5xx-code"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.lowReadabilityRate')}: ${this.auditDetails?.pageMetrics?.checks?.lowReadabilityRate ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.lowReadabilityRate === 0 ? 'PASSED' : 'NOTICE',
        "description": this.auditDetails?.pageMetrics?.checks?.lowReadabilityRate,
        "filter": "['checks.low_readability_rate', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.lowReadabilityRate'),
        "howToFixPath":"low-readability-rate"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.irrelevantTitle')}: ${this.auditDetails?.pageMetrics?.checks?.irrelevantTitle ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.irrelevantTitle === 0 ? 'PASSED' : 'NOTICE',
        "description": this.auditDetails?.pageMetrics?.checks?.irrelevantTitle,
        "filter": "['checks.irrelevant_title', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.irrelevantTitle'),
        "howToFixPath":"irrelavent-title"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.noTitle')}: ${this.auditDetails?.pageMetrics?.checks?.noTitle ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.noTitle === 0 ? 'PASSED' : 'CRITICAL',
        "description": this.auditDetails?.pageMetrics?.checks?.noTitle,
        "filter": "['checks.no_title', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.noTitle'),
        "howToFixPath":"no-title"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.hasMisspelling')}: ${this.auditDetails?.pageMetrics?.checks?.hasMisspelling ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.hasMisspelling === 0 ? 'PASSED' : 'WARNING',
        "description": this.auditDetails?.pageMetrics?.checks?.hasMisspelling,
        "filter": "",
        "title": this.translate.instant('onpage.summaryAudit.hasMisspelling'),
        "howToFixPath":"has-misspelling"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.sizeGreaterThan3mb')}: ${this.auditDetails?.pageMetrics?.checks?.sizeGreaterThan3mb ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.sizeGreaterThan3mb === 0 ? 'PASSED' : 'CRITICAL',
        "description": this.auditDetails?.pageMetrics?.checks?.sizeGreaterThan3mb,
        "filter": "['checks.size_greater_than_3mb', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.sizeGreaterThan3mb'),
        "howToFixPath":"size-greater-than-3mb"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.httpsToHttpLinks')}: ${this.auditDetails?.pageMetrics?.checks?.httpsToHttpLinks ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.httpsToHttpLinks  === 0 ? 'PASSED' : 'WARNING',
        "description": this.auditDetails?.pageMetrics?.checks?.httpsToHttpLinks,
        "filter": "['checks.https_to_http_links', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.httpsToHttpLinks'),
        "howToFixPath":"https-to-http-links"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.seoFriendlyUrl')}: ${this.auditDetails?.pageMetrics?.checks?.seoFriendlyUrl ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.seoFriendlyUrl === 0 ? 'PASSED' : 'WARNING',
        "description": this.auditDetails?.pageMetrics?.checks?.seoFriendlyUrl,
        "filter": "['checks.seo_friendly_url', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.seoFriendlyUrl'),
        "howToFixPath":"seo-friendly-url"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.irrelevantDescription')}: ${this.auditDetails?.pageMetrics?.checks?.irrelevantDescription ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.irrelevantDescription === 0 ? 'PASSED' : 'WARNING',
        "description": this.auditDetails?.pageMetrics?.checks?.irrelevantDescription,
        "filter": "['checks.irrelevant_description', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.irrelevantDescription'),
        "howToFixPath":"irrelevant-description"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.isOrphanPage')}: ${this.auditDetails?.pageMetrics?.checks?.isOrphanPage ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.isOrphanPage === 0 ? 'PASSED' : 'CRITICAL',
        "description": this.auditDetails?.pageMetrics?.checks?.isOrphanPage,
        "filter": "['checks.is_orphan_page', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.isOrphanPage'),
        "howToFixPath":"is-orphan-page"
      },
      {
        "name": `${this.translate.instant('onpage.summaryAudit.deprecatedHtmlTags')}: ${this.auditDetails?.pageMetrics?.checks?.deprecatedHtmlTags ?? 0}`,
        "indicator": this.auditDetails?.pageMetrics?.checks?.deprecatedHtmlTags === 0 ? 'PASSED' : 'NOTICE',
        "description": this.auditDetails?.pageMetrics?.checks?.deprecatedHtmlTags,
        "filter": "['checks.deprecated_html_tags', '=', true]",
        "title": this.translate.instant('onpage.summaryAudit.deprecatedHtmlTags'),
        "howToFixPath":"deprecated-html-tags"
      }
      
    ];
  }

  get scanedPages() {
    return this._scanedPages;
  }

  set scanedPages(value) {
    this._scanedPages = value;
  }
}
